import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, CalendarDays, Users, Filter,
  MapPin, Phone, X, CheckCircle2, BarChart3, Grid3x3,
} from "lucide-react";
import api, { getUser } from "@/lib/api";
import WeekView, { addDays, ymd as ymdD } from "@/components/rtsCalendar/WeekView";
import VolumeView from "@/components/rtsCalendar/VolumeView";

/**
 * Calendário mensal de Reuniões Técnicas — visão global (todos os
 * utilizadores autenticados). Cada RT aparece como pill com a cor do
 * Consultor atribuído. Filtros por consultor e por escritório.
 */
const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEKDAYS_PT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }

function daysInMonth(y, m) {
  return new Date(y, m, 0).getDate();
}

/** Return a 6×7 grid of ISO dates (Monday-first) for the given month. */
function monthGrid(y, m) {
  const first = new Date(y, m - 1, 1);
  const firstWeekday = (first.getDay() + 6) % 7; // Monday=0 … Sunday=6
  const total = daysInMonth(y, m);
  const cells = [];
  // Leading days from previous month
  for (let i = firstWeekday - 1; i >= 0; i--) {
    const d = new Date(y, m - 1, -i);
    cells.push({
      iso: ymd(d.getFullYear(), d.getMonth() + 1, d.getDate()),
      d: d.getDate(),
      outside: true,
    });
  }
  // Current month days
  for (let d = 1; d <= total; d++) {
    cells.push({ iso: ymd(y, m, d), d, outside: false });
  }
  // Trailing days
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const idx = cells.length - firstWeekday - total + 1;
    const d = new Date(y, m, idx);
    cells.push({
      iso: ymd(d.getFullYear(), d.getMonth() + 1, d.getDate()),
      d: d.getDate(),
      outside: true,
    });
    if (cells.length >= 42) break;
  }
  return cells.slice(0, 42);
}

function hhmm(iso) {
  if (!iso) return "";
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

function EventPill({ event, compact = false }) {
  const label = compact
    ? `${hhmm(event.scheduled_qt_at) || "•"} ${event.name}`
    : event.name;
  return (
    <Link
      to={`/leads/${event.id}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`event-pill-${event.id}`}
      className="block truncate text-[10.5px] leading-tight px-1.5 py-0.5 rounded border transition-colors hover:brightness-110"
      style={{
        backgroundColor: event.consultor_color + "22",
        borderColor: event.consultor_color + "60",
        color: event.consultor_color,
      }}
      title={`${event.name} · ${hhmm(event.scheduled_qt_at) || ""} · ${event.consultor_name || "Sem consultor"}`}
    >
      {event.completed && <CheckCircle2 className="inline w-2.5 h-2.5 mr-1 align-[-1px]" />}
      {label}
    </Link>
  );
}

function DayModal({ dayIso, events, onClose }) {
  if (!dayIso) return null;
  const d = new Date(dayIso + "T00:00:00");
  const title = d.toLocaleDateString("pt-PT", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="day-modal">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reuniões técnicas</div>
            <div className="font-ui text-base capitalize">{title}</div>
          </div>
          <button onClick={onClose} data-testid="day-modal-close" className="text-muted-foreground hover:text-foreground p-2">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {events.length === 0 ? (
            <div className="text-center text-muted-foreground py-10 text-sm">Nenhuma RT neste dia.</div>
          ) : events.map((e) => (
            <Link
              key={e.id}
              to={`/leads/${e.id}`}
              className="block px-5 py-3 border-b border-border hover:bg-muted/40 transition-colors"
              data-testid={`day-event-${e.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: e.consultor_color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[13px] font-semibold">{e.name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{hhmm(e.scheduled_qt_at)}</div>
                    {e.completed && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border border-[hsl(var(--success))]/30 text-[hsl(var(--success))] bg-[hsl(var(--success-bg))]">
                        <CheckCircle2 className="w-3 h-3" /> Realizada
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                    {e.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</span>}
                    {e.district && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.district}</span>}
                    {e.consultor_name && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {e.consultor_name}</span>}
                    {e.office && <span className="text-[10px] uppercase tracking-[0.15em] bg-muted/60 px-1.5 py-0.5 rounded-full">{e.office}</span>}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function RTsCalendar() {
  const now = new Date();
  const me = getUser();
  const [searchParams] = useSearchParams();
  const initialView = (searchParams.get("view") || "month");
  const [view, setView] = useState(["month", "week", "volume"].includes(initialView) ? initialView : "month");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Anchor day for the week view — Monday of the visible week.
  const initialMonday = (() => {
    const anchor = searchParams.get("anchor");
    if (anchor && /^\d{4}-\d{2}-\d{2}$/.test(anchor)) {
      const [y, m, dd] = anchor.split("-").map(Number);
      return new Date(y, m - 1, dd);
    }
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  })();
  const [weekAnchor, setWeekAnchor] = useState(initialMonday);
  const highlightIso = searchParams.get("highlight") || null;
  const [consultorId, setConsultorId] = useState("");
  const [office, setOffice] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayIso, setDayIso] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (view === "week") {
        // Fetch the full week (7 days) via range params.
        const start = ymdD(weekAnchor);
        const endD = addDays(weekAnchor, 6);
        params.set("start_date", start);
        params.set("end_date", ymdD(endD));
      } else {
        params.set("year", year);
        params.set("month", month);
      }
      if (consultorId) params.set("consultor_id", consultorId);
      if (office) params.set("office", office);
      const r = await api.get(`/rts-calendar?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar calendário");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, year, month, weekAnchor, consultorId, office]);

  const grid = useMemo(() => monthGrid(year, month), [year, month]);
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of (data?.events || [])) {
      const iso = (ev.scheduled_qt_at || "").slice(0, 10);
      if (!iso) continue;
      (map[iso] = map[iso] || []).push(ev);
    }
    return map;
  }, [data]);

  const goPrev = () => {
    if (view === "week") {
      setWeekAnchor(addDays(weekAnchor, -7));
      return;
    }
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const goNext = () => {
    if (view === "week") {
      setWeekAnchor(addDays(weekAnchor, 7));
      return;
    }
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    if (view === "week") {
      const d = new Date();
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      setWeekAnchor(d);
      return;
    }
    setYear(now.getFullYear()); setMonth(now.getMonth() + 1);
  };

  const todayIso = ymd(now.getFullYear(), now.getMonth() + 1, now.getDate());

  const weekLabel = () => {
    const end = addDays(weekAnchor, 6);
    const sm = MONTHS_PT[weekAnchor.getMonth()].slice(0, 3);
    const em = MONTHS_PT[end.getMonth()].slice(0, 3);
    if (sm === em) return `${weekAnchor.getDate()}–${end.getDate()} ${sm} ${end.getFullYear()}`;
    return `${weekAnchor.getDate()} ${sm} – ${end.getDate()} ${em} ${end.getFullYear()}`;
  };

  const offices = useMemo(() => {
    const set = new Set();
    for (const ev of (data?.events || [])) if (ev.office) set.add(ev.office);
    return [...set].sort();
  }, [data]);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6" data-testid="rts-calendar-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Reuniões Técnicas</div>
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight mt-1">
            Calendário · <span className="text-primary">
              {view === "week" ? weekLabel() : `${MONTHS_PT[month - 1]} ${year}`}
            </span>
          </h1>
          <div className="text-sm text-muted-foreground mt-1">
            Todas as RTs agendadas pelos Consultores — vista partilhada por toda a equipa.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View toggle */}
          <div className="inline-flex items-center gap-0.5 bg-card border border-border rounded-full p-0.5" data-testid="view-toggle">
            {[
              { key: "month",  label: "Mês",    Icon: Grid3x3 },
              { key: "week",   label: "Semana", Icon: CalendarDays },
              { key: "volume", label: "Volume", Icon: BarChart3 },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                data-testid={`view-${key}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                  view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
          {view !== "volume" && (
            <div className="flex items-center gap-2">
              <button onClick={goPrev} data-testid="cal-prev" className="p-2 rounded-full border border-border bg-card hover:bg-muted">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={goToday} data-testid="cal-today" className="px-3 py-1.5 rounded-full border border-border bg-card hover:bg-muted text-xs font-semibold">
                Hoje
              </button>
              <button onClick={goNext} data-testid="cal-next" className="p-2 rounded-full border border-border bg-card hover:bg-muted">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters + legend */}
      <div className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Consultor:
        </div>
        <select
          value={consultorId}
          onChange={(e) => setConsultorId(e.target.value)}
          className="bg-muted/40 border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-primary/50"
          data-testid="filter-consultor"
        >
          <option value="">Todos</option>
          {(data?.consultors || []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {offices.length > 0 && (
          <>
            <div className="inline-flex items-center gap-2 text-xs text-muted-foreground ml-2">Escritório:</div>
            <select
              value={office}
              onChange={(e) => setOffice(e.target.value)}
              className="bg-muted/40 border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-primary/50"
              data-testid="filter-office"
            >
              <option value="">Todos</option>
              {offices.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </>
        )}
        {(consultorId || office) && (
          <button onClick={() => { setConsultorId(""); setOffice(""); }} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="clear-filters">
            limpar
          </button>
        )}
        {/* Legend */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {(data?.consultors || []).slice(0, 8).map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border"
              style={{
                backgroundColor: c.color + "22",
                borderColor: c.color + "60",
                color: c.color,
              }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
              {c.name.split(" ")[0]}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> A carregar…
        </div>
      ) : view === "week" ? (
        <WeekView weekStart={weekAnchor} events={data?.events || []} todayIso={todayIso} highlightIso={highlightIso} />
      ) : view === "volume" ? (
        <VolumeView
          events={data?.events || []}
          consultors={data?.consultors || []}
          monthLabel={`${MONTHS_PT[month - 1]} ${year}`}
        />
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-border">
            {WEEKDAYS_PT.map((w) => (
              <div key={w} className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold text-center py-2">
                {w}
              </div>
            ))}
          </div>
          {/* Cells */}
          <div className="grid grid-cols-7">
            {grid.map((cell) => {
              const evs = eventsByDay[cell.iso] || [];
              const isToday = cell.iso === todayIso;
              return (
                <div
                  key={cell.iso}
                  onClick={() => (evs.length > 0 || !cell.outside) && setDayIso(cell.iso)}
                  className={`min-h-[110px] border-r border-b border-border p-1.5 cursor-pointer transition-colors ${
                    cell.outside ? "bg-muted/20 text-muted-foreground/60" : "hover:bg-muted/30"
                  } ${isToday ? "bg-primary/8" : ""}`}
                  data-testid={`cell-${cell.iso}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${isToday ? "bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center" : ""}`}>
                      {cell.d}
                    </span>
                    {evs.length > 0 && !cell.outside && (
                      <span className="text-[10px] text-muted-foreground font-mono">{evs.length}</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {evs.slice(0, 3).map((e) => (
                      <EventPill key={e.id} event={e} compact />
                    ))}
                    {evs.length > 3 && (
                      <div className="text-[10px] text-muted-foreground pl-1">+{evs.length - 3} mais</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {dayIso && (
        <DayModal
          dayIso={dayIso}
          events={eventsByDay[dayIso] || []}
          onClose={() => setDayIso(null)}
        />
      )}
    </div>
  );
}
